import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import { getPublishedArticles, getInfoGraphics, logoutGoogleAuth, verifyGoogleAuth } from '../Services/ApiCalls';
import { GoogleLogin } from '@react-oauth/google';
import { isStorageSupported } from '../utils/storageCheck';
import type { Article, Album } from '../types';


import { Link as RouterLink } from 'react-router-dom';

interface MyAppBarProps {
    router: any;
}

interface SearchOption {
    id: number;
    title: string;
    type: 'article' | 'blog' | 'album';
    slug?: string;
    category?: string;
}

function MyAppBar(props: MyAppBarProps) {
    const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
    // anchorElUser state removed as it was unused
    const [searchTerm, setSearchTerm] = React.useState('');
    const [searchOptions, setSearchOptions] = React.useState<SearchOption[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [user, setUser] = React.useState<any>(null);
    const router = props.router
    const publicLoginEnabled = process.env.REACT_APP_PUBLIC_LOGIN_ENABLED === 'true' && Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

    React.useEffect(() => {
        try {
            const savedUser = localStorage.getItem('app_user');
            if (savedUser) {
                setUser(JSON.parse(savedUser));
            }
        } catch (error) {
            console.warn('localStorage access blocked:', error);
        }
    }, []);

    // Fetch all articles and albums for search suggestions
    React.useEffect(() => {
        const fetchSearchData = async () => {
            try {
                setLoading(true);
                const [articlesRes, albumsRes] = await Promise.all([
                    getPublishedArticles(1, 100),
                    getInfoGraphics(1, 100)
                ]);

                const options: SearchOption[] = [];

                // Add articles and blogs
                (articlesRes.articles || []).forEach((article: Article) => {
                    const isBlog = article.excerpt?.toLowerCase().includes('blog');
                    options.push({
                        id: article.id,
                        title: article.title,
                        type: isBlog ? 'blog' : 'article',
                        slug: article.slug,
                    });
                });

                // Add albums
                (albumsRes.albums || []).forEach((album: Album) => {
                    options.push({
                        id: album.id,
                        title: album.title,
                        type: 'album',
                        category: album.category,
                    });
                });

                setSearchOptions(options);
            } catch (e) {
                console.error('Error fetching search data:', e);
            } finally {
                setLoading(false);
            }
        };

        fetchSearchData();
    }, []);

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };
    // handleOpenUserMenu removed

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    // handleCloseUserMenu removed
    const pages = [
        { id: 1, name: 'Info-Graphics', route: '/infographics' },
        { id: 2, name: 'Illustrations', route: '/radioillustrations' },
        { id: 3, name: 'Articles', route: '/articles' },
        { id: 4, name: 'Blogs', route: '/blogs' },
        { id: 5, name: 'About', route: '/about' },
        { id: 6, name: 'Contact', route: '/contact' },
    ];
    const handleClick = (route: string) => {
        router.navigate(route);
    }

    // handleSearch removed as it was unused

    const handleLogout = async () => {
        try {
            await logoutGoogleAuth();
        } catch (error) {
            console.error("Logout failed", error);
        }
        try {
            localStorage.removeItem('app_user');
        } catch (error) {
            console.warn('localStorage access blocked:', error);
        }
        window.location.reload();
    };

    const handleGoogleSuccess = async (credentialResponse: any) => {
        try {
            if (credentialResponse.credential) {
                const result = await verifyGoogleAuth(credentialResponse.credential);
                if (result.user) {
                    try {
                        localStorage.setItem('app_user', JSON.stringify(result.user));
                    } catch (error) {
                        console.warn('localStorage access blocked:', error);
                    }
                    setUser(result.user);
                    window.location.reload();
                }
            }
        } catch (error) {
            console.error("Google Auth failed", error);
        }
    };

    const handleGoogleError = () => {
        console.error('Google Login Failed');
    };

    return (
        <AppBar position="static" >
            <Container maxWidth="xl"  disableGutters>
                <Toolbar >
                    <Box
                        component="a"
                        href="/"
                        onClick={(e) => {
                            e.preventDefault();
                            handleClick('/');
                        }}
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            mr: 2,
                            gap: 1.5,
                        }}
                    >
                        <img
                            src={require('../assets/logo.jpg')}
                            alt="S. Arora Radiology"
                            style={{ height: 40, width: 40, borderRadius: '50%' }}
                        />
                        <img
                            src={require('../assets/mainlogo.jpg')}
                            alt="Rad Illustration"
                            style={{ 
                                height: 50, 
                                width: 'auto',
                                maxHeight: '50px',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>

                    <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{ display: { xs: 'block', md: 'none' } }}
                        >
                            {pages.map((page) => (
                                <MenuItem 
                                    key={page.id} 
                                    component={RouterLink}
                                    to={page.route}
                                    onClick={handleCloseNavMenu}
                                >
                                    <Typography sx={{ textAlign: 'center' }}>{page.name}</Typography>
                                </MenuItem>
                            ))}
                            {/* Search Bar - Mobile */}
                            <MenuItem sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', p: 2 }}>
                                <Box sx={{ width: '100%' }}>
                                    <Autocomplete
                                        freeSolo
                                        options={searchOptions}
                                        getOptionLabel={(option) => 
                                            typeof option === 'string' ? option : option.title
                                        }
                                        inputValue={searchTerm}
                                        onInputChange={(_, newInputValue) => {
                                            setSearchTerm(newInputValue);
                                        }}
                                        onChange={(_, newValue) => {
                                            if (newValue && typeof newValue !== 'string') {
                                                if (newValue.type === 'article' || newValue.type === 'blog') {
                                                    router.navigate(`/articles/${newValue.slug}`);
                                                } else if (newValue.type === 'album') {
                                                    router.navigate(`/album/${newValue.id}`);
                                                }
                                                setSearchTerm('');
                                                handleCloseNavMenu();
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && searchTerm.trim()) {
                                                e.preventDefault();
                                                router.navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
                                                setSearchTerm('');
                                                handleCloseNavMenu();
                                            }
                                        }}
                                        loading={loading}
                                        filterOptions={(options, { inputValue }) => {
                                            if (!inputValue) return [];
                                            const searchLower = inputValue.toLowerCase();
                                            return options.filter(option =>
                                                option.title.toLowerCase().includes(searchLower)
                                            ).slice(0, 5);
                                        }}
                                        renderOption={(props, option) => (
                                            <Box
                                                component="li"
                                                {...props}
                                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                            >
                                                <Typography variant="body2">{option.title}</Typography>
                                                <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                                                    {option.type === 'blog' ? 'Blog' : option.type === 'article' ? 'Article' : 'Album'}
                                                </Typography>
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                placeholder="Search..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start">
                                                                <SearchIcon />
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </MenuItem>
                            {/* Auth Section - Mobile */}
                            {publicLoginEnabled && (
                            <MenuItem sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                {user ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <Typography variant="body1">
                                            Hello, {user.name}
                                        </Typography>
                                        <Button 
                                            variant="outlined" 
                                            size="small" 
                                            onClick={handleLogout}
                                            fullWidth
                                            sx={{ 
                                                borderRadius: 2, 
                                                textTransform: 'none',
                                            }}
                                        >
                                            Logout
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                        {isStorageSupported() ? (
                                            <GoogleLogin
                                                onSuccess={handleGoogleSuccess}
                                                onError={handleGoogleError}
                                                shape="pill"
                                                size="medium"
                                                theme="outline"
                                            />
                                        ) : (
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                Login disabled in sandbox
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </MenuItem>
                            )}
                        </Menu>
                    </Box>

                    <Box
                        component="a"
                        href="/"
                        onClick={(e) => {
                            e.preventDefault();
                            handleClick('/');
                        }}
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            flexGrow: 1,
                            alignItems: 'center',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            mr: 2,
                            gap: 1,
                        }}
                    >
                        <img
                            src={require('../assets/logo.jpg')}
                            alt="S. Arora Radiology"
                            style={{ height: 35, width: 35, borderRadius: '50%' }}
                        />
                        <img
                            src={require('../assets/mainlogo.jpg')}
                            alt="Rad Illustration"
                            style={{ 
                                height: 35, 
                                width: 'auto',
                                maxHeight: '35px',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                        {pages.map((page) => (
                            <Button
                                color="inherit"
                                key={page.id}
                                component={RouterLink}
                                to={page.route}
                                sx={{ my: 2, display: 'block' }}
                            >
                                {page.name}
                            </Button>
                        ))}
                        {/* Search Bar - Desktop */}
                        <Box sx={{ ml: 'auto', maxWidth: 300, width: '100%' }}>
                            <Autocomplete
                                freeSolo
                                options={searchOptions}
                                getOptionLabel={(option) => 
                                    typeof option === 'string' ? option : option.title
                                }
                                inputValue={searchTerm}
                                onInputChange={(_, newInputValue) => {
                                    setSearchTerm(newInputValue);
                                }}
                                onChange={(_, newValue) => {
                                    if (newValue && typeof newValue !== 'string') {
                                        // User selected an option from dropdown
                                        if (newValue.type === 'article' || newValue.type === 'blog') {
                                            router.navigate(`/articles/${newValue.slug}`);
                                        } else if (newValue.type === 'album') {
                                            router.navigate(`/album/${newValue.id}`);
                                        }
                                        setSearchTerm('');
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && searchTerm.trim()) {
                                        e.preventDefault();
                                        router.navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
                                        setSearchTerm('');
                                    }
                                }}
                                loading={loading}
                                filterOptions={(options, { inputValue }) => {
                                    if (!inputValue) return [];
                                    const searchLower = inputValue.toLowerCase();
                                    return options.filter(option =>
                                        option.title.toLowerCase().includes(searchLower)
                                    ).slice(0, 5); // Limit to 5 suggestions
                                }}
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    >
                                        <Typography variant="body2">{option.title}</Typography>
                                        <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                                            {option.type === 'blog' ? 'Blog' : option.type === 'article' ? 'Article' : 'Album'}
                                        </Typography>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Search..."
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: 'inherit' }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                '& fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.7)',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                color: 'white',
                                                '&::placeholder': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                    />
                                )}
                                sx={{
                                    '& .MuiAutocomplete-popper': {
                                        zIndex: 1300,
                                    },
                                }}
                            />
                        </Box>
                        
                        {/* Auth Section */}
                        {publicLoginEnabled && (
                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            {user ? (
                                <>
                                    <Typography variant="body1" sx={{ color: 'white' }}>
                                        Hello, {user.name}
                                    </Typography>
                                    <Button 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={handleLogout}
                                        sx={{ 
                                            borderRadius: 2, 
                                            textTransform: 'none',
                                            color: 'white',
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                            '&:hover': {
                                                borderColor: 'white',
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                            }
                                        }}
                                    >
                                        Logout
                                    </Button>
                                </>
                            ) : (
                                isStorageSupported() ? (
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleError}
                                        shape="pill"
                                        size="medium"
                                        theme="filled_black"
                                    />
                                ) : (
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                        Login disabled
                                    </Typography>
                                )
                            )}
                        </Box>
                        )}
                    </Box>
      
                </Toolbar>
            </Container>
        </AppBar>
    );
}
export default MyAppBar;
